import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight,
  Facebook, Instagram, Linkedin, Clock, Edit2, Trash2,
  CheckCircle, AlertCircle, Send, X, Image, Hash, Loader2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Calendar } from '../../../components/ui/calendar';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO, isAfter } from 'date-fns';
import { fr } from 'date-fns/locale';

import { API_URL } from '../../../config/api';

// Google icon component
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const platformConfig = {
  facebook: { icon: Facebook, color: 'bg-blue-600', name: 'Facebook' },
  instagram: { icon: Instagram, color: 'bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500', name: 'Instagram' },
  linkedin: { icon: Linkedin, color: 'bg-blue-700', name: 'LinkedIn' },
  google_business: { icon: () => <GoogleIcon className="w-4 h-4" />, color: 'bg-white border border-slate-200', name: 'Google Business' }
};

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  pending_approval: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Planifie', color: 'bg-blue-100 text-blue-700' },
  published: { label: 'Publie', color: 'bg-green-100 text-green-700' },
  failed: { label: 'Echec', color: 'bg-red-100 text-red-700' },
  rejected: { label: 'Rejete', color: 'bg-red-100 text-red-700' }
};

export default function ContentCalendarPage() {
  const { user } = useOutletContext();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    fetchAccounts();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [currentMonth]);

  const fetchAccounts = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAccounts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchPosts = async () => {
    const token = localStorage.getItem('client_token');
    setLoading(true);

    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);

    try {
      const response = await fetch(
        `${API_URL}/api/marketing/calendar?start_date=${start.toISOString()}&end_date=${end.toISOString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setPosts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/calendar/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getPostsForDay = (date) => {
    return posts.filter(post => {
      const postDate = post.scheduled_at ? parseISO(post.scheduled_at) : 
                       post.published_at ? parseISO(post.published_at) : null;
      return postDate && isSameDay(postDate, date);
    });
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    if (isAfter(date, new Date()) || isSameDay(date, new Date())) {
      setShowCreateModal(true);
    }
  };

  const handlePostClick = (post, e) => {
    e.stopPropagation();
    setSelectedPost(post);
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Add padding days for week alignment
  const firstDayOfMonth = startOfMonth(currentMonth).getDay();
  const paddingDays = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  return (
    <div className="space-y-6" data-testid="content-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-rm-text">Calendrier de publication</h1>
          <p className="text-sm text-slate-500">Planifie et gère tes publications sur tous tes réseaux</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/espace-client/marketing/accounts">
            <Button variant="outline" size="sm">
              Gerer les comptes
            </Button>
          </Link>
          <Button onClick={() => { setSelectedDate(new Date()); setShowCreateModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau post
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Cette semaine</p>
            <p className="text-2xl font-bold text-rm-text">{stats.posts_this_week}</p>
            <p className="text-xs text-slate-400">publications</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Ce mois</p>
            <p className="text-2xl font-bold text-rm-text">{stats.posts_this_month}</p>
            <p className="text-xs text-slate-400">publications</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Planifies</p>
            <p className="text-2xl font-bold text-blue-600">{stats.scheduled_posts}</p>
            <p className="text-xs text-slate-400">a venir</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Meilleur moment</p>
            <p className="text-lg font-bold text-rm-text">{stats.best_times?.[0]?.day} {stats.best_times?.[0]?.time}</p>
            <p className="text-xs text-green-600">Engagement haut</p>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Month Navigation */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-lg font-semibold text-rm-text capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: fr })}
          </h2>
          <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Days Header */}
        <div className="grid grid-cols-7 border-b border-slate-100">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-slate-500 bg-slate-50">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {/* Padding days */}
          {Array.from({ length: paddingDays }).map((_, i) => (
            <div key={`pad-${i}`} className="min-h-[120px] p-2 bg-slate-50 border-b border-r border-slate-100" />
          ))}
          
          {/* Actual days */}
          {days.map((day, i) => {
            const dayPosts = getPostsForDay(day);
            const isToday = isSameDay(day, new Date());
            const isPast = !isAfter(day, new Date()) && !isToday;
            
            return (
              <div
                key={day.toISOString()}
                onClick={() => handleDayClick(day)}
                className={`min-h-[120px] p-2 border-b border-r border-slate-100 cursor-pointer transition-colors
                  ${isToday ? 'bg-blue-50' : isPast ? 'bg-slate-50' : 'hover:bg-slate-50'}
                `}
              >
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-slate-700'}`}>
                  {format(day, 'd')}
                </div>
                
                <div className="space-y-1">
                  {dayPosts.slice(0, 3).map(post => {
                    const platform = platformConfig[post.platform] || {};
                    const status = statusConfig[post.status] || {};
                    const Icon = platform.icon || CalendarIcon;
                    
                    return (
                      <div
                        key={post.id}
                        onClick={(e) => handlePostClick(post, e)}
                        className={`text-xs p-1.5 rounded flex items-center gap-1.5 ${status.color} hover:opacity-80 transition-opacity`}
                      >
                        <div className={`w-4 h-4 rounded flex items-center justify-center text-white ${platform.color}`}>
                          <Icon className="w-2.5 h-2.5" />
                        </div>
                        <span className="truncate flex-1">{post.content?.substring(0, 20)}...</span>
                      </div>
                    );
                  })}
                  {dayPosts.length > 3 && (
                    <div className="text-xs text-slate-500 pl-1">
                      +{dayPosts.length - 3} autres
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* No accounts warning */}
      {accounts.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">Aucun compte connecte</p>
            <p className="text-sm text-amber-600">
              <Link to="/espace-client/marketing/accounts" className="underline">Connecte tes réseaux sociaux</Link> pour commencer à planifier des publications.
            </p>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setSelectedDate(null); }}
        accounts={accounts}
        selectedDate={selectedDate}
        onSuccess={() => { fetchPosts(); fetchStats(); }}
      />

      {/* Post Detail Modal */}
      <PostDetailModal
        post={selectedPost}
        onClose={() => setSelectedPost(null)}
        onUpdate={() => { fetchPosts(); fetchStats(); }}
      />
    </div>
  );
}

// Create Post Modal Component
function CreatePostModal({ isOpen, onClose, accounts, selectedDate, onSuccess }) {
  const [content, setContent] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('10:00');
  const [hashtags, setHashtags] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishNow, setPublishNow] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      setScheduledDate(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (accounts.length > 0 && !selectedAccount) {
      setSelectedAccount(accounts[0].id);
    }
  }, [accounts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() || !selectedAccount) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    const token = localStorage.getItem('client_token');
    setPublishing(true);

    try {
      const scheduledAt = publishNow ? null : `${scheduledDate}T${scheduledTime}:00`;
      
      const response = await fetch(`${API_URL}/api/marketing/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: selectedAccount,
          content: content.trim(),
          hashtags: hashtags.split(' ').filter(h => h.startsWith('#')).map(h => h.substring(1)),
          scheduled_at: scheduledAt,
          publish_immediately: publishNow
        })
      });

      if (response.ok) {
        toast.success(publishNow ? 'Post publie !' : 'Post planifie !');
        setContent('');
        setHashtags('');
        onClose();
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la creation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setPublishing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
      >
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-rm-text">Nouveau post</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Account Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Compte</label>
            <div className="flex flex-wrap gap-2">
              {accounts.map(account => {
                const platform = platformConfig[account.platform] || {};
                const Icon = platform.icon || CalendarIcon;
                const isSelected = selectedAccount === account.id;
                
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => setSelectedAccount(account.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-colors ${
                      isSelected ? 'border-rm-primary bg-rm-primary/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded flex items-center justify-center text-white ${platform.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-sm font-medium">{account.platform_name}</span>
                    {isSelected && <CheckCircle className="w-4 h-4 text-rm-primary" />}
                  </button>
                );
              })}
            </div>
            {accounts.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">
                <Link to="/espace-client/marketing/accounts" className="underline">Connectez un compte</Link> pour publier
              </p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Contenu</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Que voulez-vous partager ?"
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rm-primary focus:border-transparent resize-none"
              maxLength={2200}
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>{content.length} / 2200 caracteres</span>
            </div>
          </div>

          {/* Hashtags */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Hash className="w-4 h-4 inline mr-1" />
              Hashtags
            </label>
            <input
              type="text"
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              placeholder="#batiment #renovation #artisan"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rm-primary focus:border-transparent"
            />
          </div>

          {/* Schedule Options */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="publishNow"
                checked={publishNow}
                onChange={(e) => setPublishNow(e.target.checked)}
                className="w-4 h-4 text-rm-primary rounded"
              />
              <label htmlFor="publishNow" className="text-sm font-medium text-slate-700">
                Publier immediatement
              </label>
            </div>

            {!publishNow && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Heure</label>
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={publishing || !selectedAccount}>
              {publishing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : publishNow ? (
                <Send className="w-4 h-4 mr-2" />
              ) : (
                <Clock className="w-4 h-4 mr-2" />
              )}
              {publishNow ? 'Publier' : 'Planifier'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Post Detail Modal Component
function PostDetailModal({ post, onClose, onUpdate }) {
  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState(false);

  if (!post) return null;

  const platform = platformConfig[post.platform] || {};
  const status = statusConfig[post.status] || {};
  const Icon = platform.icon || CalendarIcon;

  const handleDelete = async () => {
    if (!confirm('Supprimer ce post ?')) return;
    
    const token = localStorage.getItem('client_token');
    setDeleting(true);

    try {
      const response = await fetch(`${API_URL}/api/marketing/posts/${post.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Post supprime');
        onClose();
        onUpdate();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setDeleting(false);
    }
  };

  const handlePublishNow = async () => {
    const token = localStorage.getItem('client_token');
    setPublishing(true);

    try {
      const response = await fetch(`${API_URL}/api/marketing/posts/${post.id}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success('Post publie !');
        } else {
          toast.error(result.error || 'Erreur de publication');
        }
        onClose();
        onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de publication');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
      >
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${platform.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-rm-text">{post.account_name || platform.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                  {status.label}
                </span>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Content */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-slate-700 whitespace-pre-wrap">{post.content}</p>
          </div>

          {/* Hashtags */}
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.hashtags.map((tag, i) => (
                <span key={i} className="text-sm text-blue-600">#{tag}</span>
              ))}
            </div>
          )}

          {/* Dates */}
          <div className="text-sm text-slate-500 space-y-1">
            {post.scheduled_at && (
              <p>
                <Clock className="w-4 h-4 inline mr-1" />
                Planifie: {format(parseISO(post.scheduled_at), 'dd MMM yyyy a HH:mm', { locale: fr })}
              </p>
            )}
            {post.published_at && (
              <p>
                <CheckCircle className="w-4 h-4 inline mr-1 text-green-500" />
                Publie: {format(parseISO(post.published_at), 'dd MMM yyyy a HH:mm', { locale: fr })}
              </p>
            )}
            {post.error_message && (
              <p className="text-red-600">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Erreur: {post.error_message}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-slate-100 flex justify-between">
          <Button
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleDelete}
            disabled={deleting || post.status === 'published'}
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Supprimer
          </Button>
          
          {post.status !== 'published' && post.status !== 'failed' && (
            <Button onClick={handlePublishNow} disabled={publishing}>
              {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Publier maintenant
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
